import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * EliteKanbanBoard.jsx (React 17.0.2)
 * ✅ Trello-like Kanban with:
 *  - First column shows **Ready for Planning** (from GET /production/orders)
 *  - Columns (internal): Planned → Scheduled → In_Production → QC → Completed
 *    • UI label for Planned is "Ready for Planning".
 *  - Drag & drop with **adjacent-only** rule (n can drop to n±1 only)
 *  - Card pop-out modal (Trello-style) on click
 *  - Department filter, search, auto-refresh, optimistic updates
 *  - No external UI libs; fully self-contained CSS-in-JS
 *
 * Backend endpoints used:
 *  - GET  /production/orders?page=&pageSize=&q=            → builds Planning cards
 *  - GET  /production/kanban?departmentId=&q=              → loads WIP steps into columns
 *  - PATCH /production/steps/:id/status  { toStatus }       → move step between columns
 *  - POST /production/sync-from-orders (optional hook)      → convert order→PWO+Step when moving out of Planning
 */

// ----------------------------
// Status & labels
// ----------------------------
const STATUSES = [
  { key: "Planned",        label: "Ready for Planning" },
  { key: "Scheduled",      label: "Scheduled" },
  { key: "In_Production",  label: "In Production" },
  { key: "QC",             label: "QC" },
  { key: "Completed",      label: "Completed" },
];
const STATUS_INDEX = STATUSES.reduce((m, s, i) => (m[s.key] = i, m), {});

// Department mapping heuristics (charge name → department)
const DEPT_MATCHERS = {
  Screening:  [/screen ?print/i, /pad ?print/i, /silk/i],
  Embroidery: [/embroider/i],
  "Direct to Film": [/DTF/i],
  Engraving:  [/engrave/i, /laser/i],
  Cresting:   [/crest/i, /heat ?press/i, /vinyl/i],
  Awards:     [/award/i, /trophy/i],
};

const CHARGE_TO_DEPT = {
  ENGR: "Engraving",
  CRST: "Cresting",
  EMBR: "Embroidery",
  SCRN: "Screening",
  DTF:  "Direct to Film",
  AWDS: "Awards",
};

// ----------------------------
// Modal (Trello-like card details)
// ----------------------------
function JobCardModal({ open, onClose, data }) {
  if (!open || !data) return null;
  const S = modalStyles;
  const money = (n) => (typeof n === "number" ? n.toFixed(2) : "–");
  const Row = ({ label, value }) => (
    <div>
      <div style={S.label}>{label}</div>
      <div style={S.value}>{(value ?? value === 0) && value !== "" ? value : "–"}</div>
    </div>
  );

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.card} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <h3 style={S.h3}>Elite Core Job Card</h3>
          <button style={S.closeBtn} onClick={onClose}>Close</button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#444", flexWrap: "wrap" }}>
          <span><b>Order:</b> {data.project?.projectNumber || "–"}</span>
          <span>•</span>
          <span><b>Customer:</b> {data.context?.customer || "–"}</span>
          <span>•</span>
          <span><b>In-hands:</b> {data.context?.inHandsDate || "–"}</span>
          <span>•</span>
          <span><b>Qty:</b> {data.context?.quantity ?? "–"}</span>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Project Information</div>
          <div style={S.grid3}>
            <Row label="Project name" value={data.project?.projectName} />
            <Row label="Project number" value={data.project?.projectNumber} />
            <div>
              <div style={S.label}>PO Numbers</div>
              <div style={S.chips}>
                {(data.project?.poNumbers || []).length
                  ? data.project.poNumbers.map((p, i) => (<span key={i} style={S.chip}>{p}</span>))
                  : <span style={S.value}>–</span>}
              </div>
            </div>
          </div>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Product information</div>
          <div style={S.grid3}>
            <Row label="SKU" value={data.product?.sku} />
            <Row label="Category" value={data.product?.category} />
            <Row label="Supplier" value={data.product?.supplier} />
            <div>
              <div style={S.label}>Sizes</div>
              <div style={S.chips}>
                {(data.product?.sizes || []).length
                  ? data.product.sizes.map((s, i) => <span key={i} style={S.chip}>{s}</span>)
                  : <span style={S.value}>–</span>}
              </div>
            </div>
            <div>
              <div style={S.label}>Colors</div>
              <div style={S.chips}>
                {(data.product?.colors || []).length
                  ? data.product.colors.map((c, i) => <span key={i} style={S.chip}>{c}</span>)
                  : <span style={S.value}>–</span>}
              </div>
            </div>
            <Row label="Net cost goods" value={money(data.product?.netCostGoods)} />
            <Row label="Retail cost goods" value={money(data.product?.retailCostGoods)} />
          </div>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Decoration information</div>
          <div style={S.grid3}>
            <Row label="Deco department" value={data.decoration?.department} />
            <Row label="Matrix pricing" value={data.decoration?.matrixPricing} />
            <Row label="Location" value={data.decoration?.location} />
            <Row label="Net cost goods" value={money(data.decoration?.netCostGoods)} />
            <Row label="Retail cost goods" value={money(data.decoration?.retailCostGoods)} />
          </div>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Art information</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={S.label}>Art proof</div>
              {data.art?.artProofUrl ? (
                <a href={data.art.artProofUrl} target="_blank" rel="noreferrer" style={S.link}>Open proof</a>
              ) : (
                <div style={S.value}>–</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  card: { width: "min(760px, 94vw)", background: "#fff", borderRadius: 16, boxShadow: "0 12px 32px rgba(0,0,0,.18)", padding: 20 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  h3: { margin: 0, fontSize: 18 },
  closeBtn: { border: "1px solid #ddd", background: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  section: { border: "1px solid #eee", borderRadius: 12, padding: 14, marginTop: 12 },
  sectionTitle: { fontSize: 13, color: "#666", marginBottom: 8, fontWeight: 600, letterSpacing: .2 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
  label: { fontSize: 12, color: "#666" },
  value: { fontSize: 14, fontWeight: 500, wordBreak: "break-word" },
  chips: { display: "flex", gap: 8, flexWrap: "wrap" },
  chip: { border: "1px solid #ddd", borderRadius: 999, padding: "2px 8px", fontSize: 12, background: "#fafafa" },
  link: { fontSize: 14, textDecoration: "underline", cursor: "pointer" },
};

// ----------------------------
// Main Kanban Board Component
// ----------------------------
export default function EliteKanbanBoard2() {
  const [departmentId, setDepartmentId] = useState("");
  const [deptLabel, setDeptLabel] = useState("Screening"); // human label for planning filter
  const [search, setSearch] = useState("");
  const [columns, setColumns] = useState(() => emptyColumns());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalData, setModalData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [dragTarget, setDragTarget] = useState(null); // for visual allow/deny
  const dragRef = useRef({ cardId: null, from: null, isPlanningCard: false });

  useEffect(() => { fetchAll(); }, [departmentId]);

  // Auto refresh timer
  useEffect(() => {
    let t = null;
    if (autoRefresh) t = setInterval(fetchAll, 15000);
    return () => t && clearInterval(t);
  }, [autoRefresh, departmentId, deptLabel, search]);

  function emptyColumns() {
    const obj = {};
    STATUSES.forEach(s => { obj[s.key] = []; });
    return obj;
  }

  async function fetchAll() {
    setLoading(true); setError("");
    try {
      // 1) Kanban steps
      const qs1 = new URLSearchParams({ ...(departmentId ? { departmentId } : {}), ...(search ? { q: search } : {}) });
      const r1 = await fetch(`http://localhost:3001/production/kanban${qs1.toString() ? `?${qs1.toString()}` : ""}`);
      if (!r1.ok) throw new Error(`Kanban load failed: ${r1.status}`);
      const j1 = await r1.json();
      const normalized = normalizeKanban(j1);

      // 2) Orders → Planning cards (filtered by deptLabel, with decorations only)
      const r2 = await fetch(`http://localhost:3001/production/orders?page=1&pageSize=50${search ? `&q=${encodeURIComponent(search)}` : ""}`);
      if (!r2.ok) throw new Error(`Orders load failed: ${r2.status}`);
      const j2 = await r2.json();
      const planningCards = buildPlanningCards(j2?.data || [], deptLabel);

      // Merge planning cards into first column (Planned)
      const next = JSON.parse(JSON.stringify(normalized));
      const seen = new Set(next.Planned.map(c => String(c.id)));
      for (const pc of planningCards) {
        if (!seen.has(String(pc.id))) {
          next.Planned.unshift(pc);
          seen.add(String(pc.id));
        }
      }
      setColumns(next);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  function normalizeKanban(json) {
    // Server returns: { success, columns: ["Planned",...], items: { Planned:[...] } }
    if (Array.isArray(json?.columns) && json?.items && typeof json.items === 'object') {
      const normalized = emptyColumns();
      json.columns.forEach(k => {
        const arr = json.items[k] || [];
        normalized[k] = arr.map(hydrateFromServer(k));
      });
      return normalized;
    }
    return emptyColumns();
  }

  function hydrateFromServer(columnKey) {
    return (s) => {
      const [doneStr,totalStr] = String(s.qty || "0/0").split("/");
      const qtyDone = Number(doneStr) || 0;
      const qtyTotal = Number(totalStr) || 0;
      return {
        id: s.id,
        status: columnKey,
        orderNumber: s.title || "",
        customer: "",
        productName: s.title || "",
        sku: s.title || "",
        matrix: s.decoration || "",
        location: "",
        qtyPlanned: qtyTotal,
        qtyDone: qtyDone,
        dueDate: s.due_at || "",
        department: "",
        isPlanningCard: false,
        raw: { step: s },
      };
    };
  }

  function buildPlanningCards(orders, deptHumanLabel) {
    const tests = DEPT_MATCHERS[deptHumanLabel] || [];
    const out = [];
    for (const o of orders) {
      for (const it of (o.items || [])) {
        const decos = (it.decorations || []).filter(Boolean);
        if (!decos.length) continue; // only items with at least one decoration
        const matches = decos.filter(d => tests.some(re => re.test(d.chargeName || "")));
        if (!matches.length) continue; // prefilter by department (human label)
        const d0 = matches[0];
        out.push({
          id: `ORD-${o.orderNumber}-${it.itemId}-${d0.decorationId}`,
          status: "Planned",
          orderNumber: o.orderNumber,
          customer: o.customerName || "",
          productName: it.productName || "",
          sku: it.productId || "",
          matrix: d0.chargeName || "",
          location: d0.location || "",
          qtyPlanned: Number(it.quantity || 0) || 0,
          qtyDone: 0,
          dueDate: o.inHandsDate || "",
          department: deptHumanLabel,
          isPlanningCard: true,
          raw: { order: o, item: it, deco: d0 },
        });
      }
    }
    return out;
  }

  function canDrop(fromKey, toKey) {
    const fi = STATUS_INDEX[fromKey];
    const ti = STATUS_INDEX[toKey];
    if (fi == null || ti == null) return false;
    const delta = Math.abs(ti - fi);
    return delta === 1; // adjacent-only rule
  }

  function onDragStart(e, card, fromCol) {
    dragRef.current = { cardId: card.id, from: fromCol, isPlanningCard: !!card.isPlanningCard };
    e.dataTransfer.effectAllowed = "move";
  }

  function onDragEnter(e, colKey) {
    const { from } = dragRef.current || {};
    if (!from) return;
    setDragTarget(canDrop(from, colKey) ? colKey : "__blocked__");
  }

  function onDragOver(e, colKey) {
    e.preventDefault();
    const { from } = dragRef.current || {};
    if (!from) return;
    e.dataTransfer.dropEffect = canDrop(from, colKey) ? "move" : "none";
  }

  async function onDrop(e, toCol) {
    e.preventDefault();
    const { cardId, from, isPlanningCard } = dragRef.current || {};
    setDragTarget(null);
    if (!cardId || !from || toCol === from) return;
    if (!canDrop(from, toCol)) {
      alert("Movement restricted to adjacent columns only.");
      return;
    }

    // optimistic move in UI
    const prev = JSON.parse(JSON.stringify(columns));
    const next = JSON.parse(JSON.stringify(columns));
    const idx = next[from].findIndex((c) => String(c.id) === String(cardId));
    if (idx === -1) return;
    const [moved] = next[from].splice(idx, 1);
    moved.status = toCol;
    next[toCol].unshift(moved);
    setColumns(next);

    try {
      if (isPlanningCard && toCol !== "Planned") {
        // Convert Order→PWO+Step on first move out of Planning
        const payload = {
          orderNumber: moved.raw?.order?.orderNumber,
          itemId: moved.raw?.item?.itemId,
          decorationId: moved.raw?.deco?.decorationId,
          department: moved.department,
          toStatus: toCol,
        };
        const r = await fetch('http://localhost:3001/production/sync-from-orders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!r.ok) throw new Error(`Sync failed: ${r.status}`);
        // After conversion, reload from backend to get real step ids
        await fetchAll();
        return;
      }

      // Normal step status update
      const res = await fetch(`http://localhost:3001/production/steps/${encodeURIComponent(cardId)}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toStatus: toCol }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    } catch (err) {
      setColumns(prev); // revert
      alert(err.message || String(err));
    } finally {
      dragRef.current = { cardId: null, from: null, isPlanningCard: false };
    }
  }

  function openJobCard(card) {
    const { raw } = card;
    const o = raw?.order || {};
    const it = raw?.item || {};
    const d = raw?.deco || {};
    const dept = card.department || null;

    const job = {
      project: { projectName: o.projectName || "", projectNumber: o.orderNumber || "", poNumbers: o.poNumbers || [] },
      product: { sku: it.productId || "", sizes: it.sizes || [], colors: it.colors || [], netCostGoods: it.netCostGoods ?? null, retailCostGoods: it.retailCostGoods ?? null, supplier: it.supplierName || "", category: it.category || "" },
      decoration: { department: dept || "", matrixPricing: d.chargeName || card.matrix || "", netCostGoods: d.netCostGoods ?? null, retailCostGoods: d.retailCostGoods ?? null, location: d.location || card.location || "" },
      art: { artProofUrl: d.artProofUrl || "" },
      context: { customer: o.customerName || card.customer || "", inHandsDate: o.inHandsDate || card.dueDate || "", quantity: it.quantity ?? card.qtyPlanned ?? 0 },
    };

    setModalData(job);
    setModalOpen(true);
  }

  const totals = useMemo(() => {
    const t = {};
    STATUSES.forEach(({ key }) => {
      const arr = columns[key] || [];
      const qty = arr.reduce((sum, r) => sum + (Number(r.qtyPlanned || 0) - Number(r.qtyDone || 0)), 0);
      t[key] = { cards: arr.length, remainingQty: qty };
    });
    return t;
  }, [columns]);

  return (
    <div style={styles.wrap}>
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Production Kanban</h2>
          {loading && <span style={styles.pill}>Loading…</span>}
          {error && <span style={{ ...styles.pill, background: "#ffe8e8", color: "#b00020" }}>Error: {error}</span>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select aria-label="Department (server)" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} style={styles.select}>
            <option value="">All Departments (WIP)</option>
            <option value="ENGR">Engraving</option>
            <option value="CRST">Cresting</option>
            <option value="EMBR">Embroidery</option>
            <option value="SCRN">Screening</option>
            <option value="DTF">Direct to Film</option>
            <option value="AWDS">Awards</option>
          </select>
          <select aria-label="Planning Department" value={deptLabel} onChange={(e) => setDeptLabel(e.target.value)} style={styles.select}>
            {Object.keys(DEPT_MATCHERS).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input aria-label="Search" placeholder="Search order, customer, SKU…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") fetchAll(); }} style={styles.input} />
          <button style={styles.button} onClick={fetchAll}>Reload</button>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto refresh
          </label>
        </div>
      </header>
      <div style={styles.board}>
        {STATUSES.map(({ key, label }) => (
          <section
            key={key}
            style={{ ...styles.column, outline: dragTarget === key ? '2px solid #10b981' : dragTarget === '__blocked__' ? '2px dashed #ef4444' : 'none' }}
            onDragEnter={(e) => onDragEnter(e, key)}
            onDragOver={(e) => onDragOver(e, key)}
            onDrop={(e) => onDrop(e, key)}
          >
            <div style={styles.colHeader}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <h3 style={styles.colTitle}>{label}</h3>
                <span style={styles.meta}>{totals[key]?.cards || 0} cards</span>
                <span style={styles.meta}>| Remaining: {totals[key]?.remainingQty || 0}</span>
              </div>
            </div>

            <div style={styles.cardList}>
              {(columns[key] || []).map((c) => (
                <article key={c.id} style={styles.card}
                  draggable
                  onDragStart={(e) => onDragStart(e, c, key)}
                >
                  <div style={styles.cardTop}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div style={styles.orderLine}><b>{c.orderNumber || "—"}</b> <span style={{ color: "#667" }}>• {c.customer || "—"}</span></div>
                      <div style={styles.subtle}>{c.productName || "—"} <span style={{ color: "#8a8" }}>({c.sku || ""})</span></div>
                    </div>
                    <span style={styles.badge}>{c.department || key.replace(/_/g,' ')}</span>
                  </div>

                  <div style={styles.cardMid}>
                    <div style={styles.kv}><span style={styles.k}>Qty</span><span style={styles.v}>{c.qtyPlanned ?? 0}</span></div>
                    <div style={styles.kv}><span style={styles.k}>Done</span><span style={styles.v}>{c.qtyDone ?? 0}</span></div>
                    <div style={styles.kv}><span style={styles.k}>Due</span><span style={styles.v}>{c.dueDate ? String(c.dueDate).slice(0,10) : "—"}</span></div>
                  </div>

                  <div style={styles.progressWrap}>
                    {(() => {
                      const total = Number(c.qtyPlanned || 0) || 0;
                      const done = Math.min(Number(c.qtyDone || 0) || 0, total);
                      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                      return (
                        <div style={styles.progressBar} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                          <div style={{ ...styles.progressFill, width: `${pct}%` }} />
                          <span style={styles.progressText}>{pct}%</span>
                        </div>
                      );
                    })()}
                  </div>

                  <div style={styles.cardBottom}>
                    <div style={styles.subtle}>Loc: {c.location || "—"} • Matrix: {c.matrix || "—"}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={styles.linkBtn} onClick={() => openJobCard(c)}>Open</button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
      <JobCardModal open={modalOpen} onClose={() => setModalOpen(false)} data={modalData} />
    </div>
  );
}

// ----------------------------
// Styles
// ----------------------------
const styles = {
  wrap: { padding: 16, fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif", color: "#1a1a1a" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" },
  board: { display: "grid", gridTemplateColumns: "repeat(5, minmax(260px, 1fr))", gap: 12, alignItems: "start" },
  column: { background: "#f8f9fb", border: "1px solid #eef0f5", borderRadius: 12, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 180px)" },
  colHeader: { position: "sticky", top: 0, background: "#f8f9fb", borderBottom: "1px solid #eef0f5", borderTopLeftRadius: 12, borderTopRightRadius: 12, padding: 12, zIndex: 1 },
  colTitle: { margin: 0, fontSize: 16 },
  meta: { fontSize: 12, color: "#667" },
  cardList: { padding: 10, overflow: "auto" },

  card: { background: "#fff", border: "1px solid #eef0f5", borderRadius: 12, padding: 12, marginBottom: 10, boxShadow: "0 2px 6px rgba(10,10,10,0.04)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 },
  orderLine: { fontSize: 14 },
  subtle: { fontSize: 12, color: "#667" },
  badge: { background: "#eef7ff", color: "#175cd3", border: "1px solid #d6e8ff", borderRadius: 999, padding: "2px 8px", fontSize: 12, whiteSpace: "nowrap" },

  cardMid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 6 },
  kv: { display: "flex", flexDirection: "column", gap: 2 },
  k: { fontSize: 11, color: "#667" },
  v: { fontSize: 13, fontWeight: 600 },

  progressWrap: { marginTop: 8 },
  progressBar: { position: "relative", height: 8, background: "#f0f3f8", borderRadius: 999, overflow: "hidden" },
  progressFill: { position: "absolute", top: 0, left: 0, bottom: 0, background: "#22c55e" },
  progressText: { position: "absolute", right: 6, top: -18, fontSize: 11, color: "#555" },

  cardBottom: { marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between" },
  linkBtn: { border: "1px solid #e5e7eb", background: "#fff", padding: "4px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer" },

  pill: { background: "#eef7ff", color: "#175cd3", border: "1px solid #d6e8ff", borderRadius: 999, padding: "2px 8px", fontSize: 12 },
  select: { border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 8px", fontSize: 14, background: "#fff" },
  input: { border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 8px", fontSize: 14, width: 260 },
  button: { border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", background: "#fff", cursor: "pointer", fontSize: 14 },
};
